// src/components/Comments.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useWeb3 } from '../contexts/Web3Context';
import { useNotification } from '../contexts/NotificationContext';
import axios from 'axios';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import {
  UserCircleIcon,
  PencilIcon,
  TrashIcon,
  PaperAirplaneIcon,
  ChatBubbleLeftIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  WalletIcon,
} from '@heroicons/react/24/outline';
import LoadingSpinner from './LoadingSpinner';

const Comments = ({ proposalId }) => {
  const { account, isOasysNetwork, connectWallet, switchToOasysNetwork, signMessage } = useWeb3();
  const { showNotification } = useNotification();

  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editText, setEditText] = useState('');
  const [replyToCommentId, setReplyToCommentId] = useState(null);
  const [replyText, setReplyText] = useState('');

  // コメント一覧を取得
  useEffect(() => {
    const fetchComments = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // APIエンドポイントの構築とリクエスト
        const apiUrl = `${process.env.REACT_APP_API_URL}/api/comments/proposal/${proposalId}`;
        console.log('Fetching comments from:', apiUrl);

        const response = await axios.get(apiUrl);
        console.log('Comments API response:', response.data);

        // レスポンスデータが配列でない場合の処理
        if (!Array.isArray(response.data)) {
          console.error('Invalid comments data format:', response.data);
          setComments([]);
          setIsLoading(false);
          return;
        }

        // コメントを親子関係で整理
        const commentsMap = {};
        const rootComments = [];

        // 全コメントをマップに入れる
        response.data.forEach(comment => {
          commentsMap[comment.id] = {
            ...comment,
            replies: [],
          };
        });

        // 親子関係を構築
        response.data.forEach(comment => {
          if (comment.parentId) {
            // 親コメントが存在する場合は子コメントとして追加
            if (commentsMap[comment.parentId]) {
              commentsMap[comment.parentId].replies.push(commentsMap[comment.id]);
            } else {
              // 親が見つからない場合はルートコメントとして扱う
              rootComments.push(commentsMap[comment.id]);
            }
          } else {
            // 親コメントがない場合はルートコメント
            rootComments.push(commentsMap[comment.id]);
          }
        });

        // 日付でソート（新しい順）
        rootComments.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        setComments(rootComments);
        setIsLoading(false);
      } catch (error) {
        console.error('コメント取得エラー:', error);
        setError('コメントの取得に失敗しました。しばらくしてから再度お試しください。');
        setIsLoading(false);

        // エラーの詳細をログに出力
        if (error.response) {
          console.error('Error response:', error.response.data);
          console.error('Error status:', error.response.status);
        } else if (error.request) {
          console.error('Error request:', error.request);
        } else {
          console.error('Error message:', error.message);
        }
      }
    };

    if (proposalId) {
      fetchComments();
    }
  }, [proposalId]);

  // コメント投稿
  const handleSubmitComment = async e => {
    e.preventDefault();

    if (!commentText.trim()) {
      showNotification('コメントを入力してください', 'error');
      return;
    }

    if (!account) {
      showNotification('ウォレットを接続してください', 'error');
      return;
    }

    if (!isOasysNetwork) {
      showNotification('OASYSネットワークに切り替えてください', 'error');
      return;
    }

    try {
      setIsSubmitting(true);

      // 署名メッセージの作成
      const message = `コメントを投稿します\n提案ID: ${proposalId}\n${Date.now()}`;

      // 署名
      const signature = await signMessage(message);

      // コメントデータの準備
      const commentData = {
        proposalId,
        content: commentText,
        parentId: null,
        signature,
        message,
      };

      // コメント送信
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/comments`,
        commentData
      );

      // 新しいコメントを追加
      const newComment = {
        ...response.data,
        replies: [],
      };

      setComments([newComment, ...comments]);
      setCommentText('');
      showNotification('コメントが投稿されました', 'success');
    } catch (error) {
      console.error('コメント投稿エラー:', error);
      showNotification(error.response?.data?.error || 'コメントの投稿に失敗しました', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 返信の投稿
  const handleSubmitReply = async parentId => {
    if (!replyText.trim()) {
      showNotification('返信を入力してください', 'error');
      return;
    }

    if (!account) {
      showNotification('ウォレットを接続してください', 'error');
      return;
    }

    try {
      setIsSubmitting(true);

      // 署名メッセージの作成
      const message = `返信を投稿します\n提案ID: ${proposalId}\n親コメントID: ${parentId}\n${Date.now()}`;

      // 署名
      const signature = await signMessage(message);

      // 返信データの準備
      const replyData = {
        proposalId,
        content: replyText,
        parentId,
        signature,
        message,
      };

      // 返信送信
      // const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/comments`, replyData);
      await axios.post(`${process.env.REACT_APP_API_URL}/api/comments`, replyData);

      // コメント一覧を再取得
      const commentsResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/comments/proposal/${proposalId}`
      );

      // コメントを親子関係で整理（再取得後の処理）
      const commentsMap = {};
      const rootComments = [];

      commentsResponse.data.forEach(comment => {
        commentsMap[comment.id] = {
          ...comment,
          replies: [],
        };
      });

      commentsResponse.data.forEach(comment => {
        if (comment.parentId) {
          if (commentsMap[comment.parentId]) {
            commentsMap[comment.parentId].replies.push(commentsMap[comment.id]);
          } else {
            rootComments.push(commentsMap[comment.id]);
          }
        } else {
          rootComments.push(commentsMap[comment.id]);
        }
      });

      rootComments.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setComments(rootComments);
      setReplyText('');
      setReplyToCommentId(null);
      showNotification('返信が投稿されました', 'success');
    } catch (error) {
      console.error('返信投稿エラー:', error);
      showNotification(error.response?.data?.error || '返信の投稿に失敗しました', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // コメント編集
  const handleEditComment = async commentId => {
    if (!editText.trim()) {
      showNotification('コメントを入力してください', 'error');
      return;
    }

    try {
      setIsSubmitting(true);

      // 署名メッセージの作成
      const message = `コメントを編集します\nコメントID: ${commentId}\n${Date.now()}`;

      // 署名
      const signature = await signMessage(message);

      // 編集データの準備
      const editData = {
        content: editText,
        signature,
        message,
      };

      // コメント編集
      await axios.put(`${process.env.REACT_APP_API_URL}/api/comments/${commentId}`, editData);

      // コメント一覧を更新
      const updatedComments = [...comments];

      // 該当コメントを検索して更新する関数
      const updateComment = commentsArray => {
        for (let i = 0; i < commentsArray.length; i++) {
          if (commentsArray[i].id === commentId) {
            commentsArray[i].content = editText;
            return true;
          }

          if (commentsArray[i].replies && commentsArray[i].replies.length > 0) {
            if (updateComment(commentsArray[i].replies)) {
              return true;
            }
          }
        }

        return false;
      };

      updateComment(updatedComments);

      setComments(updatedComments);
      setEditingCommentId(null);
      setEditText('');
      showNotification('コメントが更新されました', 'success');
    } catch (error) {
      console.error('コメント編集エラー:', error);
      showNotification(error.response?.data?.error || 'コメントの編集に失敗しました', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // コメント削除
  const handleDeleteComment = async commentId => {
    if (!confirm('このコメントを削除してもよろしいですか？')) {
      return;
    }

    try {
      // 署名メッセージの作成
      const message = `コメントを削除します\nコメントID: ${commentId}\n${Date.now()}`;

      // 署名
      const signature = await signMessage(message);

      // 削除リクエスト
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/comments/${commentId}`, {
        data: {
          signature,
          message,
        },
      });

      // コメント一覧から削除
      const removeComment = commentsArray => {
        // 直接の子要素をフィルタリング
        const filtered = commentsArray.filter(comment => comment.id !== commentId);

        // 残ったコメントの返信をチェック
        filtered.forEach(comment => {
          if (comment.replies && comment.replies.length > 0) {
            comment.replies = removeComment(comment.replies);
          }
        });

        return filtered;
      };

      const updatedComments = removeComment(comments);
      setComments(updatedComments);

      showNotification('コメントが削除されました', 'success');
    } catch (error) {
      console.error('コメント削除エラー:', error);
      showNotification(error.response?.data?.error || 'コメントの削除に失敗しました', 'error');
    }
  };

  // 投票（賛成/反対）
  const handleVoteComment = async (commentId, voteType) => {
    if (!account) {
      showNotification('ウォレットを接続してください', 'error');
      return;
    }

    try {
      // 署名メッセージの作成
      const message = `コメントに${
        voteType === 'up' ? '賛成' : '反対'
      }します\nコメントID: ${commentId}\n${Date.now()}`;

      // 署名
      const signature = await signMessage(message);

      // 投票データの準備
      const voteData = {
        voteType,
        signature,
        message,
      };

      // 投票送信
      await axios.post(`${process.env.REACT_APP_API_URL}/api/comments/${commentId}/vote`, voteData);

      // コメント一覧を更新
      const updatedComments = [...comments];

      // 該当コメントを検索して投票数を更新する関数
      const updateCommentVotes = commentsArray => {
        for (let i = 0; i < commentsArray.length; i++) {
          if (commentsArray[i].id === commentId) {
            if (voteType === 'up') {
              commentsArray[i].upVotes = (commentsArray[i].upVotes || 0) + 1;
            } else {
              commentsArray[i].downVotes = (commentsArray[i].downVotes || 0) + 1;
            }
            return true;
          }

          if (commentsArray[i].replies && commentsArray[i].replies.length > 0) {
            if (updateCommentVotes(commentsArray[i].replies)) {
              return true;
            }
          }
        }

        return false;
      };

      updateCommentVotes(updatedComments);

      setComments(updatedComments);
      showNotification(`コメントに${voteType === 'up' ? '賛成' : '反対'}しました`, 'success');
    } catch (error) {
      console.error('コメント投票エラー:', error);
      showNotification(error.response?.data?.error || 'コメントへの投票に失敗しました', 'error');
    }
  };

  // ウォレット接続ハンドラー
  const handleConnect = async () => {
    if (!account) {
      await connectWallet();
    } else if (!isOasysNetwork) {
      await switchToOasysNetwork();
    }
  };

  // アドレスを短縮
  const shortenAddress = address => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  // コメントコンポーネント（再帰的に使用）
  const CommentItem = ({ comment, level = 0 }) => {
    const isCommentOwner = account && comment.authorAddress === account;
    const maxLevel = 3; // 返信の最大階層

    return (
      <div
        className={`border-l-2 ${
          level === 0
            ? 'border-gray-300 dark:border-gray-600'
            : 'border-gray-200 dark:border-gray-700'
        } pl-4 mb-4`}
      >
        <div className="bg-white dark:bg-gray-800 rounded-md p-4 shadow-sm">
          {/* コメントヘッダー */}
          <div className="flex justify-between items-start mb-2">
            <div className="flex items-center">
              <div className="h-8 w-8 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-300 mr-2">
                <UserCircleIcon className="h-5 w-5" />
              </div>
              <div>
                <Link
                  to={`/user/${comment.authorAddress}`}
                  className="font-medium text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400"
                >
                  {comment.authorName || shortenAddress(comment.authorAddress)}
                </Link>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {format(new Date(comment.createdAt), 'yyyy/MM/dd HH:mm', { locale: ja })}
                </div>
              </div>
            </div>

            {/* 編集・削除ボタン（コメントの作成者のみ表示） */}
            {isCommentOwner && (
              <div className="flex space-x-2">
                {editingCommentId !== comment.id ? (
                  <>
                    <button
                      onClick={() => {
                        setEditingCommentId(comment.id);
                        setEditText(comment.content);
                      }}
                      className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteComment(comment.id)}
                      className="text-red-500 hover:text-red-700 dark:hover:text-red-300"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setEditingCommentId(null)}
                    className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    キャンセル
                  </button>
                )}
              </div>
            )}
          </div>

          {/* コメント内容（編集モードかどうかで表示を切り替え） */}
          {editingCommentId === comment.id ? (
            <div>
              <textarea
                value={editText}
                onChange={e => setEditText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                rows="3"
              />
              <div className="flex justify-end mt-2">
                <button
                  onClick={() => handleEditComment(comment.id)}
                  disabled={isSubmitting}
                  className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 dark:disabled:bg-blue-800"
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">保存中...</span>
                    </span>
                  ) : (
                    '保存'
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="text-gray-800 dark:text-gray-200">{comment.content}</div>
          )}

          {/* 投票とリアクション */}
          <div className="flex items-center mt-3 text-sm">
            <button
              onClick={() => handleVoteComment(comment.id, 'up')}
              className="flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mr-3"
            >
              <ChevronUpIcon className="h-4 w-4 mr-1" />
              <span>{comment.upVotes || 0}</span>
            </button>
            <button
              onClick={() => handleVoteComment(comment.id, 'down')}
              className="flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mr-4"
            >
              <ChevronDownIcon className="h-4 w-4 mr-1" />
              <span>{comment.downVotes || 0}</span>
            </button>

            {/* 返信ボタン（最大レベルに達していない場合のみ表示） */}
            {level < maxLevel && (
              <button
                onClick={() =>
                  setReplyToCommentId(replyToCommentId === comment.id ? null : comment.id)
                }
                className="flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <ChatBubbleLeftIcon className="h-4 w-4 mr-1" />
                <span>返信</span>
              </button>
            )}
          </div>

          {/* 返信フォーム */}
          {replyToCommentId === comment.id && (
            <div className="mt-3 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
              {!account ? (
                <div className="text-center py-3 flex flex-col items-center justify-center">
                  <p className="mb-2 text-sm">返信するにはウォレットを接続してください</p>
                  <button
                    onClick={handleConnect}
                    className="flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                  >
                    <WalletIcon className="h-4 w-4 mr-1" />
                    ウォレット接続
                  </button>
                </div>
              ) : !isOasysNetwork ? (
                <div className="text-center py-3 flex flex-col items-center justify-center">
                  <p className="mb-2 text-sm">OASYSネットワークに切り替えてください</p>
                  <button
                    onClick={switchToOasysNetwork}
                    className="flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                  >
                    <svg
                      className="h-4 w-4 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    OASYSネットワークに切替
                  </button>
                </div>
              ) : (
                <div>
                  <textarea
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    placeholder="返信を入力..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    rows="2"
                  />
                  <div className="flex justify-end mt-2">
                    <button
                      onClick={() => setReplyToCommentId(null)}
                      className="px-3 py-1 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 mr-2"
                    >
                      キャンセル
                    </button>
                    <button
                      onClick={() => handleSubmitReply(comment.id)}
                      disabled={isSubmitting || !replyText.trim()}
                      className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 dark:disabled:bg-blue-800 flex items-center"
                    >
                      {isSubmitting ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <>
                          <PaperAirplaneIcon className="h-4 w-4 mr-1" />
                          返信する
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 返信コメント（再帰的に表示） */}
        {comment.replies && comment.replies.length > 0 && (
          <div className="mt-2 ml-4">
            {comment.replies.map(reply => (
              <CommentItem key={reply.id} comment={reply} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* コメント投稿フォーム */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
        <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">コメントを投稿</h3>

        {!account ? (
          <div className="text-center py-4 flex flex-col items-center justify-center">
            <p className="mb-4">コメントするにはウォレットを接続してください</p>
            <button
              onClick={handleConnect}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <WalletIcon className="h-5 w-5 mr-2" />
              ウォレット接続
            </button>
          </div>
        ) : !isOasysNetwork ? (
          <div className="text-center py-4 flex flex-col items-center justify-center">
            <p className="mb-4">コメントするにはOASYSネットワークに切り替えてください</p>
            <button
              onClick={switchToOasysNetwork}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              OASYSネットワークに切替
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmitComment}>
            <textarea
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              placeholder="コメントを入力..."
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              rows="3"
              required
            />

            <div className="flex justify-end mt-3">
              <button
                type="submit"
                disabled={isSubmitting || !commentText.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 dark:disabled:bg-blue-800 flex items-center"
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <LoadingSpinner size="sm" />
                    <span className="ml-2">投稿中...</span>
                  </span>
                ) : (
                  <>
                    <PaperAirplaneIcon className="h-5 w-5 mr-2" />
                    コメントを投稿
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* コメント一覧 */}
      <div>
        <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">コメント一覧</h3>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 px-4 py-3 rounded">
            {error}
          </div>
        ) : comments.length === 0 ? (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-8 text-center">
            <ChatBubbleLeftIcon className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">まだコメントはありません</p>
            <p className="text-gray-500 dark:text-gray-400 mt-2">
              最初のコメントを投稿しましょう！
            </p>
          </div>
        ) : (
          <div>
            {comments.map(comment => (
              <CommentItem key={comment.id} comment={comment} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Comments;
